import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Calendar,
  Search,
  Trophy,
  Loader2,
  ChevronDown,
  AlertTriangle,
  Award,
  BookOpen,
  HeartHandshake,
  CheckCircle2,
  Sparkles,
  Layers,
  GraduationCap,
  Star,
  Download,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { useInstitution } from '../../contexts/InstitutionContext';
import { useToast } from '../../Components/Feedback';
import { Input, Badge } from '../../Components/ui';
import { API_URL } from '../../api/config';

const normalizar = (s) =>
  String(s || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

const isoHoje = () => new Date().toISOString().split('T')[0];
const isoDiasAtras = (dias) => {
  const d = new Date();
  d.setDate(d.getDate() - dias);
  return d.toISOString().split('T')[0];
};
const isoPrimeiroDiaMes = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const formatarDataCurta = (iso) => {
  if (!iso) return '';
  const partes = String(iso).split('T')[0].split('-');
  if (partes.length >= 3) return `${partes[2]}/${partes[1]}`;
  return iso;
};

const formatarNivel = (aluno) =>
  aluno.nivel == null
    ? ''
    : `Nível ${aluno.subnivel ? `${aluno.nivel}.${aluno.subnivel}` : aluno.nivel}`;

const ATALHOS_PERIODO = [
  { label: 'Esta semana', calcular: () => ({ inicio: isoDiasAtras(6), fim: isoHoje() }) },
  { label: 'Este mês', calcular: () => ({ inicio: isoPrimeiroDiaMes(), fim: isoHoje() }) },
  { label: 'Últimos 30 dias', calcular: () => ({ inicio: isoDiasAtras(29), fim: isoHoje() }) },
];

const corGravidade = {
  boa: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border border-emerald-200/50',
  muito_boa: 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 border border-teal-200/50',
  leve: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200/50',
  media: 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 border border-orange-200/50',
  grave: 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200/50',
  gravissima: 'bg-rose-100 dark:bg-rose-900/40 text-rose-800 dark:text-rose-300 border border-rose-300/50',
};

const labelGravidade = {
  boa: 'Boa (+0,2)',
  muito_boa: 'Muito Boa (+0,4)',
  leve: 'Mal - Leve (-0,2)',
  media: 'Mal - Média (-0,4)',
  grave: 'Mal - Grave (-10,0)',
  gravissima: 'Mal - Gravíssima (-10,0)',
};

function formatarNota(n) {
  if (n === null || n === undefined) return '—';
  return Number(n).toFixed(1);
}

function formatarItemGrade(n) {
  if (n === null || n === undefined) {
    return <span className="text-gray-400 dark:text-gray-500 italic text-[10px]">Não cursa</span>;
  }
  if (Number(n) === 0) {
    return <span className="text-amber-600 dark:text-amber-400 font-medium text-[10px]">0,0 (Sem nota)</span>;
  }
  return <span className="font-semibold text-gray-900 dark:text-gray-200">{Number(n).toFixed(1)}</span>;
}

// Avaliação Unificada 360° de Meritocracia:
// Escala padronizada de 0,0 a 10,0 dentro de cada período avaliativo (3 pilares: Performance, Assiduidade, Comportamento).
// Quando múltiplos períodos estão selecionados, as notas finais de cada período são SOMADAS (e não tirada a média entre eles).
function Meritocracia() {
  const { institutionId } = useInstitution();
  const toast = useToast();

  const [periodos, setPeriodos] = useState([]);
  const [carregandoPeriodos, setCarregandoPeriodos] = useState(false);
  const [periodosSelecionados, setPeriodosSelecionados] = useState([]);
  const [modoManual, setModoManual] = useState(false);

  const [dataInicio, setDataInicio] = useState(isoPrimeiroDiaMes());
  const [dataFim, setDataFim] = useState(isoHoje());

  const [alunos, setAlunos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busca, setBusca] = useState('');
  const [expandido, setExpandido] = useState(null);
  const [apenasBolsistas, setApenasBolsistas] = useState(false);
  const [apenasLideres, setApenasLideres] = useState(false);

  // Busca dados de meritocracia para um intervalo de datas
  const requisitarMeritocracia = useCallback(
    async (inicio, fim) => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${API_URL}/api/alunos/meritocracia?inicio=${inicio}&fim=${fim}`,
        {
          headers: {
            'x-institution-id': institutionId,
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || 'Erro ao consultar meritocracia no período.');
      }
      return data?.data ?? (Array.isArray(data) ? data : []);
    },
    [institutionId],
  );

  // Carrega a lista de períodos cadastrados na tela de Notas
  useEffect(() => {
    if (!institutionId) return;

    let cancelado = false;
    const carregarPeriodos = async () => {
      setCarregandoPeriodos(true);
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_URL}/api/notas/periodos`, {
          headers: {
            'x-institution-id': institutionId,
            Authorization: `Bearer ${token}`,
          },
        });
        const body = await response.json();
        const lista = body?.data ?? (Array.isArray(body) ? body : []);

        // Filtra apenas períodos com início e fim válidos
        const periodosValidos = Array.isArray(lista)
          ? lista.filter((p) => p && p.id && p.nome && p.data_inicio && p.data_fim)
          : [];

        // Ordena cronologicamente por data de início
        periodosValidos.sort((a, b) =>
          String(a.data_inicio).localeCompare(String(b.data_inicio)),
        );

        if (!cancelado) {
          setPeriodos(periodosValidos);
          if (periodosValidos.length > 0) {
            // Seleciona o período vigente ou o primeiro por padrão
            const hoje = isoHoje();
            const periodoAtual = periodosValidos.find(
              (p) => String(p.data_inicio).slice(0, 10) <= hoje && String(p.data_fim).slice(0, 10) >= hoje,
            );
            const padrao = periodoAtual || periodosValidos[0];
            setPeriodosSelecionados([padrao.id]);
          }
        }
      } catch (err) {
        console.error('Erro ao carregar períodos avaliativos:', err);
      } finally {
        if (!cancelado) setCarregandoPeriodos(false);
      }
    };

    carregarPeriodos();
    return () => {
      cancelado = true;
    };
  }, [institutionId]);

  // Função principal de cálculo (regras de botões e soma entre períodos)
  const calcularMeritocraciaPeriodos = useCallback(
    async (idsSelecionados, listaPeriodos = periodos) => {
      if (!institutionId) return;
      if (!idsSelecionados || idsSelecionados.length === 0) {
        setAlunos([]);
        return;
      }

      setLoading(true);
      try {
        const periodosAtivos = listaPeriodos.filter((p) => idsSelecionados.includes(p.id));
        if (periodosAtivos.length === 0) {
          setAlunos([]);
          return;
        }

        // Busca dados de cada período em paralelo
        const resultados = await Promise.all(
          periodosAtivos.map(async (p) => {
            const inicio = String(p.data_inicio).slice(0, 10);
            const fim = String(p.data_fim).slice(0, 10);
            const listaAlunos = await requisitarMeritocracia(inicio, fim);
            return { periodo: p, alunos: listaAlunos };
          }),
        );

        // Se for exatamente 1 período selecionado: exibe a média do período (0 a 10)
        if (resultados.length === 1) {
          const unico = resultados[0];
          const formatados = unico.alunos.map((a) => ({
            ...a,
            periodos_detalhe: [
              {
                periodo_id: unico.periodo.id,
                periodo_nome: unico.periodo.nome,
                nota_final: a.nota_final,
                performance: a.performance,
                assiduidade: a.assiduidade,
                comportamento: a.comportamento,
                dias_presentes: a.dias_presentes,
                dias_esperados: a.dias_esperados,
                perdeu_meritocracia: a.perdeu_meritocracia,
                detalhes: a.detalhes,
              },
            ],
          }));
          setAlunos(formatados);
          return;
        }

        // Se forem 2 ou mais períodos selecionados:
        // Dentro de cada período a nota é de 0 a 10, mas entre os períodos os resultados são SOMADOS.
        const mapaAlunos = new Map();

        for (const res of resultados) {
          const p = res.periodo;
          for (const a of res.alunos) {
            if (!mapaAlunos.has(a.id)) {
              mapaAlunos.set(a.id, {
                id: a.id,
                nome: a.nome,
                turno: a.turno,
                nivel: a.nivel,
                subnivel: a.subnivel,
                bolsista: Boolean(a.bolsista),
                lider_servidor: Boolean(a.lider_servidor),
                nota_final: 0,
                pontos_somados: 0,
                performance_soma: 0,
                performance_count: 0,
                assiduidade_soma: 0,
                comportamento_soma: 0,
                dias_presentes: 0,
                dias_esperados: 0,
                perdeu_meritocracia_em_algum: false,
                perdeu_meritocracia_em_todos: true,
                ocorrencias: [],
                atos_carater: [],
                periodos_detalhe: [],
                detalhes_performance: {
                  educacao_soma: 0,
                  educacao_cursou: false,
                  esporte_soma: 0,
                  esporte_cursou: false,
                  cultura_soma: 0,
                  cultura_cursou: false,
                  pratica_soma: 0,
                  pratica_cursou: false,
                  teorica_soma: 0,
                  teorica_cursou: false,
                  danca_soma: 0,
                  danca_cursou: false,
                  disciplinas_cursadas: new Set(),
                },
                detalhes_comportamento: {
                  base_soma: 0,
                  saldo_soma: 0,
                  pontos_carater_soma: 0,
                  atos_carater_total: 0,
                },
              });
            }

            const item = mapaAlunos.get(a.id);
            const notaPeriodo = a.perdeu_meritocracia ? 0.0 : (Number(a.nota_final) || 0.0);

            item.nota_final += notaPeriodo;
            item.pontos_somados += notaPeriodo;

            const perfNum =
              a.performance !== null && a.performance !== undefined ? Number(a.performance) : 0.0;
            item.performance_soma += perfNum;
            item.performance_count += 1;

            item.assiduidade_soma += Number(a.assiduidade || 0);
            item.comportamento_soma += Number(a.comportamento || 0);
            item.dias_presentes += Number(a.dias_presentes || 0);
            item.dias_esperados += Number(a.dias_esperados || 0);

            // Consolidação das matérias específicas (Educação, Esporte, Cultura)
            const perf = a.detalhes?.performance;
            if (perf) {
              const dp = item.detalhes_performance;
              if (perf.educacao !== null && perf.educacao !== undefined) {
                dp.educacao_soma += Number(perf.educacao);
                dp.educacao_cursou = true;
              }
              if (perf.esporte !== null && perf.esporte !== undefined) {
                dp.esporte_soma += Number(perf.esporte);
                dp.esporte_cursou = true;
              }
              if (perf.cultura !== null && perf.cultura !== undefined) {
                dp.cultura_soma += Number(perf.cultura);
                dp.cultura_cursou = true;
              }
              const partes = perf.cultura_partes;
              if (partes) {
                if (partes.pratica !== null && partes.pratica !== undefined) {
                  dp.pratica_soma += Number(partes.pratica);
                  dp.pratica_cursou = true;
                }
                if (partes.teorica !== null && partes.teorica !== undefined) {
                  dp.teorica_soma += Number(partes.teorica);
                  dp.teorica_cursou = true;
                }
                if (partes.danca !== null && partes.danca !== undefined) {
                  dp.danca_soma += Number(partes.danca);
                  dp.danca_cursou = true;
                }
              }
              if (Array.isArray(perf.disciplinas_cursadas)) {
                perf.disciplinas_cursadas.forEach((d) => dp.disciplinas_cursadas.add(d));
              }
            }

            // Consolidação dos dados de comportamento
            const comp = a.detalhes?.comportamento;
            if (comp) {
              item.detalhes_comportamento.base_soma += Number(comp.base ?? 7.0);
              item.detalhes_comportamento.saldo_soma += Number(comp.saldo ?? 0);
              item.detalhes_comportamento.pontos_carater_soma += Number(comp.pontos_carater ?? 0);
              item.detalhes_comportamento.atos_carater_total += Number(comp.atos_carater_total ?? 0);
            } else {
              item.detalhes_comportamento.base_soma += 7.0;
            }

            if (a.perdeu_meritocracia) {
              item.perdeu_meritocracia_em_algum = true;
            } else {
              item.perdeu_meritocracia_em_todos = false;
            }

            if (Array.isArray(a.ocorrencias)) {
              item.ocorrencias.push(
                ...a.ocorrencias.map((o) => ({ ...o, periodo_nome: p.nome })),
              );
            }
            if (Array.isArray(a.atos_carater)) {
              item.atos_carater.push(
                ...a.atos_carater.map((c) => ({ ...c, periodo_nome: p.nome })),
              );
            }

            item.periodos_detalhe.push({
              periodo_id: p.id,
              periodo_nome: p.nome,
              nota_final: a.nota_final,
              performance: a.performance,
              assiduidade: a.assiduidade,
              comportamento: a.comportamento,
              dias_presentes: a.dias_presentes,
              dias_esperados: a.dias_esperados,
              perdeu_meritocracia: a.perdeu_meritocracia,
              detalhes: a.detalhes,
            });
          }
        }

        const listaConsolidada = Array.from(mapaAlunos.values()).map((item) => {
          const notaSomada = Math.round(item.nota_final * 10) / 10;
          const dp = item.detalhes_performance;
          const algumCursou =
            dp &&
            (dp.educacao_cursou ||
              dp.esporte_cursou ||
              dp.cultura_cursou ||
              dp.pratica_cursou ||
              dp.teorica_cursou ||
              dp.danca_cursou);

          const detalhesConsolidados = {
            performance: {
              nao_avaliado: !algumCursou,
              educacao: dp?.educacao_cursou ? Math.round(dp.educacao_soma * 10) / 10 : null,
              esporte: dp?.esporte_cursou ? Math.round(dp.esporte_soma * 10) / 10 : null,
              cultura: dp?.cultura_cursou ? Math.round(dp.cultura_soma * 10) / 10 : null,
              cultura_partes: {
                pratica: dp?.pratica_cursou ? Math.round(dp.pratica_soma * 10) / 10 : null,
                teorica: dp?.teorica_cursou ? Math.round(dp.teorica_soma * 10) / 10 : null,
                danca: dp?.danca_cursou ? Math.round(dp.danca_soma * 10) / 10 : null,
              },
              disciplinas_cursadas: dp ? Array.from(dp.disciplinas_cursadas) : [],
            },
            assiduidade: {
              dias_presentes: item.dias_presentes,
              dias_esperados: item.dias_esperados,
              porcentagem:
                item.dias_esperados > 0
                  ? Math.min(100, Math.max(0, Math.round((item.dias_presentes / item.dias_esperados) * 100)))
                  : 0,
            },
            comportamento: {
              base: Math.round(item.detalhes_comportamento.base_soma * 10) / 10,
              saldo: Math.round(item.detalhes_comportamento.saldo_soma * 10) / 10,
              pontos_carater: Math.round(item.detalhes_comportamento.pontos_carater_soma * 10) / 10,
              atos_carater_total: item.detalhes_comportamento.atos_carater_total,
            },
          };

          return {
            ...item,
            nota_final: notaSomada,
            pontos_finais: notaSomada,
            performance:
              item.performance_count > 0 ? Math.round(item.performance_soma * 10) / 10 : null,
            assiduidade: Math.round(item.assiduidade_soma * 10) / 10,
            comportamento: Math.round(item.comportamento_soma * 10) / 10,
            detalhes: detalhesConsolidados,
            perdeu_meritocracia: item.perdeu_meritocracia_em_todos,
            perdeu_em_algum: item.perdeu_meritocracia_em_algum,
          };
        });

        // Ordena com maior pontuação somada no topo
        listaConsolidada.sort((a, b) => {
          if (a.perdeu_meritocracia !== b.perdeu_meritocracia) {
            return a.perdeu_meritocracia ? 1 : -1;
          }
          return (
            b.nota_final - a.nota_final ||
            b.assiduidade - a.assiduidade ||
            (b.performance ?? 0) - (a.performance ?? 0) ||
            b.comportamento - a.comportamento ||
            a.nome.localeCompare(b.nome, 'pt-BR')
          );
        });

        setAlunos(listaConsolidada);
      } catch (error) {
        console.error('Erro ao calcular meritocracia somada:', error);
        toast.error('Erro ao buscar dados: ' + (error.message || 'Erro desconhecido'));
      } finally {
        setLoading(false);
      }
    },
    [institutionId, periodos, requisitarMeritocracia, toast],
  );

  // Alterna o estado de seleção de um botão de período
  const alternarPeriodo = (id) => {
    setModoManual(false);
    const novos = periodosSelecionados.includes(id)
      ? periodosSelecionados.filter((pId) => pId !== id)
      : [...periodosSelecionados, id];

    setPeriodosSelecionados(novos);
    calcularMeritocraciaPeriodos(novos);
  };

  // Alterna entre selecionar todos ou limpar
  const toggleTodosPeriodos = () => {
    setModoManual(false);
    if (periodosSelecionados.length === periodos.length) {
      setPeriodosSelecionados([]);
      setAlunos([]);
    } else {
      const todos = periodos.map((p) => p.id);
      setPeriodosSelecionados(todos);
      calcularMeritocraciaPeriodos(todos);
    }
  };

  // Busca manual por datas (ou atalhos legados)
  const buscarAlunosAssiduos = async (inicio = dataInicio, fim = dataFim) => {
    if (!inicio || !fim) {
      toast.warning('Por favor, selecione as datas de início e fim!');
      return;
    }
    setLoading(true);
    try {
      const lista = await requisitarMeritocracia(inicio, fim);
      setAlunos(lista);
    } catch (error) {
      console.error('Erro na requisição manual:', error);
      toast.error('Erro ao buscar dados: ' + (error.message || 'Erro desconhecido'));
    } finally {
      setLoading(false);
    }
  };

  // Dispara busca inicial ou quando os períodos são carregados
  useEffect(() => {
    if (!institutionId) return;

    if (!modoManual && periodos.length > 0 && periodosSelecionados.length > 0) {
      calcularMeritocraciaPeriodos(periodosSelecionados);
    } else if (modoManual || periodos.length === 0) {
      buscarAlunosAssiduos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institutionId, periodos.length]);

  const aplicarAtalho = (atalho) => {
    setModoManual(true);
    const { inicio, fim } = atalho.calcular();
    setDataInicio(inicio);
    setDataFim(fim);
    buscarAlunosAssiduos(inicio, fim);
  };

  const totalBolsistas = useMemo(
    () => alunos.filter((a) => a.bolsista).length,
    [alunos],
  );

  const totalLideres = useMemo(
    () => alunos.filter((a) => a.lider_servidor).length,
    [alunos],
  );

  // Atribui a posição original do ranking calculada para o período selecionado
  // garantindo que, ao filtrar por busca ou categoria, cada aluno preserve sua posição real no período
  const alunosComPosicao = useMemo(() => {
    return alunos.map((aluno, index) => ({
      ...aluno,
      posicao_ranking: index + 1,
    }));
  }, [alunos]);

  const buscaNorm = normalizar(busca.trim());
  const alunosFiltrados = useMemo(() => {
    return alunosComPosicao.filter((a) => {
      if (apenasBolsistas && !a.bolsista) return false;
      if (apenasLideres && !a.lider_servidor) return false;
      if (buscaNorm && !normalizar(a.nome).includes(buscaNorm)) return false;
      return true;
    });
  }, [alunosComPosicao, buscaNorm, apenasBolsistas, apenasLideres]);

  const exportarRelatorioBolsistas = () => {
    const listaBolsistas = alunosComPosicao.filter((a) => a.bolsista);
    if (listaBolsistas.length === 0) {
      toast.warning('Nenhum aluno bolsista encontrado no ranking atual.');
      return;
    }

    const periodoTexto = !modoManual
      ? periodos
          .filter((p) => periodosSelecionados.includes(p.id))
          .map((p) => p.nome)
          .join(', ') || 'Período Vigente'
      : `${formatarDataCurta(dataInicio)} a ${formatarDataCurta(dataFim)}`;

    const linhas = listaBolsistas.map((a, idx) => ({
      'Posição (Bolsistas)': `${idx + 1}º`,
      'Posição Geral (Período)': `${a.posicao_ranking}º`,
      'Aluno': a.nome,
      'Turno': a.turno || '-',
      'Nível': formatarNivel(a) || '-',
      'Nota Final (Meritocracia)': a.perdeu_meritocracia ? '0,0 (Desclassificado)' : formatarNota(a.nota_final),
      'Performance': a.performance !== null && a.performance !== undefined ? formatarNota(a.performance) : 'Não avaliado',
      'Assiduidade (Nota)': formatarNota(a.assiduidade),
      'Assiduidade (%)': a.detalhes?.assiduidade?.porcentagem != null ? `${a.detalhes.assiduidade.porcentagem}%` : '-',
      'Presenças': a.dias_presentes || 0,
      'Dias Esperados': a.dias_esperados || 0,
      'Comportamento (Nota)': formatarNota(a.comportamento),
      'Situação': a.perdeu_meritocracia ? 'Perdeu Meritocracia (Ocorrência Grave)' : 'Elegível',
      'Período Avaliado': periodoTexto,
    }));

    const worksheet = XLSX.utils.json_to_sheet(linhas);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Meritocracia Bolsistas');

    const dataHoje = new Date().toISOString().split('T')[0];
    XLSX.writeFile(workbook, `Meritocracia_Bolsistas_${dataHoje}.xlsx`);
    toast.success(`Relatório de ${listaBolsistas.length} bolsistas exportado com sucesso!`);
  };

  const exportarRelatorioLideres = () => {
    const listaLideres = alunosComPosicao.filter((a) => a.lider_servidor);
    if (listaLideres.length === 0) {
      toast.warning('Nenhum aluno líder servidor encontrado no ranking atual.');
      return;
    }

    const periodoTexto = !modoManual
      ? periodos
          .filter((p) => periodosSelecionados.includes(p.id))
          .map((p) => p.nome)
          .join(', ') || 'Período Vigente'
      : `${formatarDataCurta(dataInicio)} a ${formatarDataCurta(dataFim)}`;

    const linhas = listaLideres.map((a, idx) => ({
      'Posição (Líderes)': `${idx + 1}º`,
      'Posição Geral (Período)': `${a.posicao_ranking}º`,
      'Aluno': a.nome,
      'Turno': a.turno || '-',
      'Nível': formatarNivel(a) || '-',
      'Nota Final (Meritocracia)': a.perdeu_meritocracia ? '0,0 (Desclassificado)' : formatarNota(a.nota_final),
      'Performance': a.performance !== null && a.performance !== undefined ? formatarNota(a.performance) : 'Não avaliado',
      'Assiduidade (Nota)': formatarNota(a.assiduidade),
      'Assiduidade (%)': a.detalhes?.assiduidade?.porcentagem != null ? `${a.detalhes.assiduidade.porcentagem}%` : '-',
      'Presenças': a.dias_presentes || 0,
      'Dias Esperados': a.dias_esperados || 0,
      'Comportamento (Nota)': formatarNota(a.comportamento),
      'Situação': a.perdeu_meritocracia ? 'Perdeu Meritocracia (Ocorrência Grave)' : 'Elegível',
      'Período Avaliado': periodoTexto,
    }));

    const worksheet = XLSX.utils.json_to_sheet(linhas);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Líderes Servidores');

    const dataHoje = new Date().toISOString().split('T')[0];
    XLSX.writeFile(workbook, `Meritocracia_Lideres_Servidores_${dataHoje}.xlsx`);
    toast.success(`Relatório de ${listaLideres.length} líderes servidores exportado com sucesso!`);
  };

  const totalPeriodosAtivos = !modoManual ? periodosSelecionados.length : 1;
  const escalaMaxima = totalPeriodosAtivos > 1 ? totalPeriodosAtivos * 10 : 10;

  return (
    <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950">
      <main className="p-4 max-w-5xl mx-auto space-y-6">
        {/* Banner Explicativo da Avaliação 360° */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3.5 rounded-2xl bg-gradient-to-r from-blue-50/80 via-indigo-50/80 to-purple-50/80 dark:from-blue-950/30 dark:via-indigo-950/30 dark:to-purple-950/30 border border-blue-100/90 dark:border-blue-900/40 text-xs">
          <div className="flex items-start gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center text-blue-600 dark:text-blue-300 font-bold shrink-0">
              <BookOpen className="w-3.5 h-3.5" />
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">
                1. Performance (0 a 10)
              </p>
              <p className="text-gray-600 dark:text-gray-400 text-[11px] leading-relaxed mt-0.5">
                Média de Educação, Esporte e Cultura (sendo Cultura a média entre Prática, Teórica e Dança).
              </p>
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center text-indigo-600 dark:text-indigo-300 font-bold shrink-0">
              <Award className="w-3.5 h-3.5" />
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">
                2. Assiduidade (0 a 10)
              </p>
              <p className="text-gray-600 dark:text-gray-400 text-[11px] leading-relaxed mt-0.5">
                Calculada individualmente sobre os dias em que o aluno está escalado para comparecer no período (100% = nota 10,0).
              </p>
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center text-purple-600 dark:text-purple-300 font-bold shrink-0">
              <HeartHandshake className="w-3.5 h-3.5" />
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">
                3. Comportamento (0 a 10)
              </p>
              <p className="text-gray-600 dark:text-gray-400 text-[11px] leading-relaxed mt-0.5">
                Base 7,0 com ajustes por décimos (+0,2 / +0,4 / -0,2 / -0,4) e atos de caráter (+0,2). Falta grave zera a nota.
              </p>
            </div>
          </div>
        </div>

        {/* Card de Filtro: Seleção de Períodos com Botões ou Datas Manuais */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-3.5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
                <Calendar className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Períodos de Avaliação
                </h2>
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  {!modoManual && periodos.length > 0 ? (
                    periodosSelecionados.length === 0 ? (
                      'Nenhum período ativo. Clique em um botão abaixo para acender.'
                    ) : periodosSelecionados.length === 1 ? (
                      '1 período ativo • Média de 0,0 a 10,0'
                    ) : (
                      <span className="font-medium text-blue-600 dark:text-blue-400">
                        {periodosSelecionados.length} períodos ativos • Resultados SOMADOS (escala acumulada até {escalaMaxima},0 pts)
                      </span>
                    )
                  ) : (
                    'Avaliação por intervalo de datas manuais'
                  )}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {periodos.length > 1 && !modoManual && (
                <button
                  type="button"
                  onClick={toggleTodosPeriodos}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline font-medium cursor-pointer"
                >
                  {periodosSelecionados.length === periodos.length
                    ? 'Desmarcar todos'
                    : 'Selecionar todos'}
                </button>
              )}
              {periodos.length > 0 && (
                <button
                  type="button"
                  onClick={() => setModoManual((prev) => !prev)}
                  className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline cursor-pointer"
                >
                  {modoManual ? 'Usar botões de períodos' : 'Filtro por datas manuais'}
                </button>
              )}
            </div>
          </div>

          {/* Seletor com Botões de Período (mesmos da tela de Notas) */}
          {!modoManual && (
            <div>
              {carregandoPeriodos ? (
                <div className="flex items-center gap-2 text-xs text-gray-500 py-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-600" />
                  <span>Carregando períodos da instituição...</span>
                </div>
              ) : periodos.length === 0 ? (
                <div className="py-2 text-xs text-amber-600 dark:text-amber-400 bg-amber-50/60 dark:bg-amber-950/20 p-2.5 rounded-xl border border-amber-200/60 dark:border-amber-900/30">
                  Nenhum período avaliativo cadastrado na instituição. Cadastre os períodos na tela de <strong>Lançamento de Notas</strong> ou utilize o seletor de datas manuais abaixo.
                </div>
              ) : (
                <div className="flex items-center gap-2 flex-wrap pt-1">
                  {periodos.map((p) => {
                    const ativo = periodosSelecionados.includes(p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => alternarPeriodo(p.id)}
                        className={`px-3.5 py-2 rounded-xl text-xs font-semibold transition-all flex items-center gap-2 cursor-pointer shadow-xs ${
                          ativo
                            ? 'bg-blue-600 text-white shadow-blue-500/25 ring-2 ring-blue-500/40 ring-offset-1 dark:ring-offset-gray-900'
                            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750'
                        }`}
                      >
                        <span
                          className={`w-2 h-2 rounded-full transition-colors ${
                            ativo
                              ? 'bg-emerald-300 shadow-[0_0_6px_rgba(110,231,183,0.9)] animate-pulse'
                              : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                        />
                        <span>{p.nome}</span>
                        {p.data_inicio && p.data_fim && (
                          <span
                            className={`text-[10px] font-normal ${
                              ativo ? 'text-blue-100' : 'text-gray-400 dark:text-gray-500'
                            }`}
                          >
                            ({formatarDataCurta(p.data_inicio)} a {formatarDataCurta(p.data_fim)})
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Filtro por Datas Manuais e Atalhos (modo manual ou fallback) */}
          {(modoManual || periodos.length === 0) && (
            <div className="pt-2 border-t border-gray-100 dark:border-gray-800 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <div className="space-y-1">
                  <label
                    htmlFor="data-inicio"
                    className="text-xs font-medium text-gray-500 dark:text-gray-400"
                  >
                    Início
                  </label>
                  <input
                    id="data-inicio"
                    type="date"
                    value={dataInicio}
                    onChange={(e) => setDataInicio(e.target.value)}
                    className="w-full h-9 px-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label
                    htmlFor="data-fim"
                    className="text-xs font-medium text-gray-500 dark:text-gray-400"
                  >
                    Fim
                  </label>
                  <input
                    id="data-fim"
                    type="date"
                    value={dataFim}
                    onChange={(e) => setDataFim(e.target.value)}
                    className="w-full h-9 px-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  />
                </div>
                <button
                  onClick={() => buscarAlunosAssiduos(dataInicio, dataFim)}
                  disabled={loading}
                  className="h-9 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 shadow-sm cursor-pointer"
                >
                  {loading ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Search className="w-3.5 h-3.5" />
                  )}
                  {loading ? 'Buscando...' : 'Calcular Meritocracia'}
                </button>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                {ATALHOS_PERIODO.map((atalho) => (
                  <button
                    key={atalho.label}
                    type="button"
                    onClick={() => aplicarAtalho(atalho)}
                    className="px-2.5 h-6 rounded-lg text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors cursor-pointer"
                  >
                    {atalho.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Resultados do Ranking */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm">
          <div className="px-3.5 py-2.5 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <Trophy className="w-4 h-4 text-amber-500" />
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Ranking Unificado de Meritocracia
              </h2>
              {!loading && (
                <Badge tone="neutral">
                  {alunosFiltrados.length} {alunosFiltrados.length === 1 ? 'aluno' : 'alunos'}
                </Badge>
              )}
              {totalPeriodosAtivos > 1 && !loading && (
                <Badge tone="primary">
                  Soma de {totalPeriodosAtivos} períodos
                </Badge>
              )}
            </div>

            <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
              {/* Botões de Filtro: Todos vs Bolsistas vs Líderes Servidores */}
              <div className="flex items-center p-0.5 rounded-xl bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    setApenasBolsistas(false);
                    setApenasLideres(false);
                  }}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition-all cursor-pointer ${
                    !apenasBolsistas && !apenasLideres
                      ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-xs font-semibold'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  Todos ({alunos.length})
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setApenasBolsistas(true);
                    setApenasLideres(false);
                  }}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 cursor-pointer ${
                    apenasBolsistas
                      ? 'bg-blue-600 text-white shadow-xs font-semibold'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  <GraduationCap className="w-3.5 h-3.5" />
                  Bolsistas ({totalBolsistas})
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setApenasLideres(true);
                    setApenasBolsistas(false);
                  }}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 cursor-pointer ${
                    apenasLideres
                      ? 'bg-amber-500 dark:bg-amber-600 text-white shadow-xs font-semibold'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  <Star className="w-3.5 h-3.5 fill-current" />
                  Líderes Servidores ({totalLideres})
                </button>
              </div>

              {/* Botão de Exportar Relatório Excel */}
              {apenasLideres ? (
                <button
                  type="button"
                  onClick={exportarRelatorioLideres}
                  className="h-8 px-2.5 rounded-xl border border-amber-200 dark:border-amber-800/80 bg-amber-50 dark:bg-amber-950/40 hover:bg-amber-100 dark:hover:bg-amber-900/40 text-xs font-medium text-amber-800 dark:text-amber-200 transition-all flex items-center gap-1.5 shadow-xs cursor-pointer"
                  title="Exportar Relatório em Excel dos Líderes Servidores"
                >
                  <Download className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                  <span className="hidden sm:inline">Exportar Líderes</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={exportarRelatorioBolsistas}
                  className="h-8 px-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750 text-xs font-medium text-gray-700 dark:text-gray-300 transition-all flex items-center gap-1.5 shadow-xs cursor-pointer"
                  title="Exportar Relatório em Excel dos Bolsistas"
                >
                  <Download className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                  <span className="hidden sm:inline">Exportar Bolsistas</span>
                </button>
              )}

              <div className="w-full sm:w-48">
                <Input
                  placeholder="Buscar aluno por nome..."
                  icon={Search}
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  size="sm"
                />
              </div>
            </div>
          </div>

          <div className="p-3">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin mr-3 text-blue-600" />
                <span className="text-sm font-medium">Processando notas, presenças e ocorrências...</span>
              </div>
            ) : !modoManual && periodos.length > 0 && periodosSelecionados.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <Calendar className="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-gray-700" />
                <p className="text-sm font-medium">Nenhum período selecionado.</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Clique em um ou mais botões de período acima para acendê-los e visualizar o ranking.
                </p>
              </div>
            ) : alunosFiltrados.length > 0 ? (
              <div className="space-y-2.5">
                {alunosFiltrados.map((aluno, idx) => {
                  const aberto = expandido === aluno.id;
                  const rankPosicao = aluno.posicao_ranking ?? (idx + 1);
                  const isTop1 = rankPosicao === 1 && !aluno.perdeu_meritocracia;
                  const isTop2 = rankPosicao === 2 && !aluno.perdeu_meritocracia;
                  const isTop3 = rankPosicao === 3 && !aluno.perdeu_meritocracia;

                  let rankBadgeClass =
                    'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300';
                  if (aluno.perdeu_meritocracia) {
                    rankBadgeClass =
                      'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800';
                  } else if (isTop1) {
                    rankBadgeClass =
                      'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-700';
                  } else if (isTop2) {
                    rankBadgeClass =
                      'bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-700';
                  } else if (isTop3) {
                    rankBadgeClass =
                      'bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-400 border border-amber-400 dark:border-amber-800';
                  }

                  return (
                    <div
                      key={aluno.id}
                      className={`rounded-2xl transition-all border ${
                        aluno.perdeu_meritocracia
                          ? 'bg-red-50/30 dark:bg-red-950/10 border-red-200/70 dark:border-red-900/30'
                          : 'bg-white dark:bg-gray-900/60 border-gray-100 dark:border-gray-800 hover:border-blue-200 dark:hover:border-blue-900/50'
                      } overflow-hidden shadow-xs`}
                    >
                      <div
                        className="p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3 cursor-pointer select-none"
                        onClick={() => setExpandido(aberto ? null : aluno.id)}
                      >
                        {/* Identificação do Aluno */}
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className={`w-9 h-9 rounded-xl flex items-center justify-center text-xs font-bold shrink-0 shadow-xs ${rankBadgeClass}`}
                          >
                            {aluno.perdeu_meritocracia ? (
                              <AlertTriangle className="w-4 h-4 text-red-600" />
                            ) : (
                              `${rankPosicao}º`
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <h3
                                className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate"
                                translate="no"
                              >
                                {aluno.nome}
                              </h3>
                              {aluno.bolsista && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 text-[10px] font-semibold border border-blue-200/50 dark:border-blue-900/50 shrink-0">
                                  <GraduationCap className="w-3 h-3" /> Bolsista
                                </span>
                              )}
                              {Boolean(aluno.lider_servidor) && (
                                <span
                                  title="Líder Servidor: Aluno ajudante e referência positiva"
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-50 dark:bg-amber-950/70 text-amber-700 dark:text-amber-300 text-[10px] font-semibold border border-amber-200 dark:border-amber-800/80 shrink-0 select-none"
                                >
                                  <Star className="w-3 h-3 fill-amber-400 text-amber-500" /> Líder Servidor
                                </span>
                              )}
                              {aluno.perdeu_meritocracia && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-[10px] font-semibold uppercase tracking-wider">
                                  Perdeu Meritocracia
                                </span>
                              )}
                              {!aluno.perdeu_meritocracia && aluno.perdeu_em_algum && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-[10px] font-medium">
                                  Zerou em 1 período
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {[aluno.turno, formatarNivel(aluno)].filter(Boolean).join(' • ')}
                              {(aluno.turno || formatarNivel(aluno)) && ' • '}
                              {aluno.dias_presentes} {totalPeriodosAtivos > 1 ? 'presenças somadas' : 'presenças no período'}
                            </p>
                          </div>
                        </div>

                        {/* Pilares e Nota / Soma Final */}
                        <div className="flex items-center gap-2.5 flex-wrap shrink-0 justify-between md:justify-end">
                          {/* Mini-Chips dos 3 Pilares */}
                          <div className="flex items-center gap-1.5 flex-wrap text-xs">
                            <span
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-medium"
                              title={
                                (aluno.performance ?? 0) > 0
                                  ? 'Performance pedagógica'
                                  : 'Sem disciplinas cursadas no período (Nota 0,0)'
                              }
                            >
                              <BookOpen className="w-3 h-3 text-blue-500" />
                              Perf: {formatarNota(aluno.performance ?? 0.0)}
                            </span>
                            <span
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 font-medium"
                              title="Assiduidade sobre os dias escalados"
                            >
                              <Award className="w-3 h-3 text-indigo-500" />
                              Assid: {formatarNota(aluno.assiduidade)}
                            </span>
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg font-medium ${
                                aluno.comportamento >= 9.0
                                  ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
                                  : aluno.comportamento >= 7.0
                                    ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
                                    : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                              }`}
                              title="Comportamento"
                            >
                              <HeartHandshake className="w-3 h-3 text-emerald-500" />
                              Comp: {formatarNota(aluno.comportamento)}
                            </span>
                          </div>

                          {/* Nota Final: Quando 1 período mostra "Média: X.X", quando 2+ mostra "Soma: X.X pts" */}
                          {aluno.perdeu_meritocracia ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-xl bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 font-bold text-xs border border-red-200 dark:border-red-800">
                              0,0
                            </span>
                          ) : totalPeriodosAtivos > 1 ? (
                            <span
                              className="inline-flex items-center px-3 py-1 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold text-xs shadow-xs"
                              title={`Soma de ${totalPeriodosAtivos} períodos (Máximo possível: ${escalaMaxima},0 pts)`}
                            >
                              Soma: {formatarNota(aluno.nota_final)} pts
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-3 py-1 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold text-xs shadow-xs">
                              Média: {formatarNota(aluno.nota_final)}
                            </span>
                          )}

                          <ChevronDown
                            className={`w-4 h-4 text-gray-400 transition-transform ${aberto ? 'rotate-180' : ''}`}
                          />
                        </div>
                      </div>

                      {/* Gaveta Detalhada de Avaliação 360° */}
                      {aberto && (
                        <div className="px-3.5 pb-3.5 pt-2 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30 space-y-3">
                          {aluno.perdeu_meritocracia && (
                            <div className="p-2.5 rounded-xl bg-red-100/80 dark:bg-red-950/40 border border-red-200 dark:border-red-900/60 flex items-start gap-2.5 text-xs text-red-800 dark:text-red-300">
                              <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
                              <div>
                                <p className="font-semibold">Desclassificado da Meritocracia</p>
                                <p className="text-[11px] mt-0.5 leading-relaxed">
                                  Este aluno cometeu uma infração grave ou gravíssima registrada no período avaliativo, resultando na perda da nota de comportamento e desclassificação da meritocracia.
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Se múltiplos períodos selecionados: exibe discriminação período por período */}
                          {aluno.periodos_detalhe && aluno.periodos_detalhe.length > 1 && (
                            <div className="p-3 rounded-xl bg-white dark:bg-gray-800/80 border border-blue-100 dark:border-blue-900/40 space-y-2">
                              <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700 pb-1.5">
                                <span className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-1.5 text-xs">
                                  <Layers className="w-3.5 h-3.5 text-blue-600" />
                                  Pontuação Discriminada por Período
                                </span>
                                <span className="text-xs font-bold text-blue-600 dark:text-blue-400">
                                  Soma Total: {formatarNota(aluno.nota_final)} / {escalaMaxima},0 pts
                                </span>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 pt-1">
                                {aluno.periodos_detalhe.map((pd, pIdx) => (
                                  <div
                                    key={`pd-${pIdx}`}
                                    className={`p-2.5 rounded-lg border space-y-1 text-xs ${
                                      pd.perdeu_meritocracia
                                        ? 'bg-red-50/50 dark:bg-red-950/20 border-red-200 dark:border-red-900/40'
                                        : 'bg-gray-50 dark:bg-gray-900/50 border-gray-100 dark:border-gray-800'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <span className="font-semibold text-gray-800 dark:text-gray-200">
                                        {pd.periodo_nome}
                                      </span>
                                      <span
                                        className={`font-bold ${
                                          pd.perdeu_meritocracia
                                            ? 'text-red-600'
                                            : 'text-blue-600 dark:text-blue-400'
                                        }`}
                                      >
                                        {pd.perdeu_meritocracia ? '0,0' : formatarNota(pd.nota_final)}
                                      </span>
                                    </div>
                                    <div className="text-[10px] text-gray-500 dark:text-gray-400 space-y-0.5">
                                      <div className="flex justify-between">
                                        <span>Performance:</span>
                                        <span>{pd.performance !== null ? formatarNota(pd.performance) : '—'}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span>Assiduidade:</span>
                                        <span>{formatarNota(pd.assiduidade)}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span>Comportamento:</span>
                                        <span>{formatarNota(pd.comportamento)}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span>Presenças:</span>
                                        <span>{pd.dias_presentes} dias</span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                            {/* Card Performance Detalhada */}
                            <div className="p-3 rounded-xl bg-white dark:bg-gray-800/80 border border-gray-100 dark:border-gray-700/60 space-y-2">
                              <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700 pb-1.5">
                                <span className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                                  <BookOpen className="w-3.5 h-3.5 text-blue-600" />
                                  {totalPeriodosAtivos > 1 ? 'Performance (Soma)' : 'Performance'}
                                </span>
                                <span className="font-bold text-blue-600 dark:text-blue-400">
                                  {formatarNota(aluno.performance ?? 0.0)}
                                </span>
                              </div>
                              {aluno.detalhes?.performance?.nao_avaliado ? (
                                <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed py-1">
                                  Aluno sem disciplinas avaliativas na grade ativa no período (Performance 0,0 na média dos 3 pilares).
                                </p>
                              ) : (
                                <div className="space-y-1 text-gray-600 dark:text-gray-400 text-[11px]">
                                  <div className="flex justify-between items-center">
                                    <span>Educação{totalPeriodosAtivos > 1 ? ' (Soma)' : ''}:</span>
                                    {formatarItemGrade(aluno.detalhes?.performance?.educacao)}
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span>Esporte{totalPeriodosAtivos > 1 ? ' (Soma)' : ''}:</span>
                                    {formatarItemGrade(aluno.detalhes?.performance?.esporte)}
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span>{totalPeriodosAtivos > 1 ? 'Cultura (Soma):' : 'Cultura (Média):'}</span>
                                    {formatarItemGrade(aluno.detalhes?.performance?.cultura)}
                                  </div>
                                  <div className="pl-2 border-l-2 border-gray-200 dark:border-gray-700 space-y-0.5 text-[10px] text-gray-500 dark:text-gray-400">
                                    <div className="flex justify-between items-center">
                                      <span>Prática:</span>
                                      {formatarItemGrade(aluno.detalhes?.performance?.cultura_partes?.pratica)}
                                    </div>
                                    <div className="flex justify-between items-center">
                                      <span>Teórica:</span>
                                      {formatarItemGrade(aluno.detalhes?.performance?.cultura_partes?.teorica)}
                                    </div>
                                    <div className="flex justify-between items-center">
                                      <span>Dança:</span>
                                      {formatarItemGrade(aluno.detalhes?.performance?.cultura_partes?.danca)}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Card Assiduidade Detalhada */}
                            <div className="p-3 rounded-xl bg-white dark:bg-gray-800/80 border border-gray-100 dark:border-gray-700/60 space-y-2">
                              <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700 pb-1.5">
                                <span className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                                  <Award className="w-3.5 h-3.5 text-indigo-600" /> Assiduidade
                                </span>
                                <span className="font-bold text-indigo-600 dark:text-indigo-400">
                                  {formatarNota(aluno.assiduidade)}
                                </span>
                              </div>
                              <div className="space-y-1.5 text-gray-600 dark:text-gray-400 text-[11px]">
                                <div className="flex justify-between">
                                  <span>Presenças do aluno:</span>
                                  <span className="font-medium text-gray-900 dark:text-gray-200">
                                    {aluno.dias_presentes} dias
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Dias escalados no período:</span>
                                  <span className="font-medium text-gray-900 dark:text-gray-200">
                                    {aluno.detalhes?.assiduidade?.dias_esperados ?? aluno.dias_esperados ?? aluno.dias_presentes} dias
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Frequência no período:</span>
                                  <span className="font-medium text-indigo-600 dark:text-indigo-400">
                                    {aluno.detalhes?.assiduidade?.porcentagem ?? 0}%
                                  </span>
                                </div>
                                <p className="text-[10px] text-gray-400 dark:text-gray-500 pt-1">
                                  * Calculada individualmente sobre os dias programados na grade do aluno.
                                </p>
                              </div>
                            </div>

                            {/* Card Comportamento Detalhado */}
                            <div className="p-3 rounded-xl bg-white dark:bg-gray-800/80 border border-gray-100 dark:border-gray-700/60 space-y-2">
                              <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700 pb-1.5">
                                <span className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                                  <HeartHandshake className="w-3.5 h-3.5 text-emerald-600" /> Comportamento
                                </span>
                                <span
                                  className={`font-bold ${
                                    aluno.perdeu_meritocracia
                                      ? 'text-red-600 dark:text-red-400'
                                      : 'text-emerald-600 dark:text-emerald-400'
                                  }`}
                                >
                                  {formatarNota(aluno.comportamento)}
                                </span>
                              </div>
                              <div className="space-y-1.5 text-gray-600 dark:text-gray-400 text-[11px]">
                                <div className="flex justify-between">
                                  <span>Base de comportamento:</span>
                                  <span className="font-medium text-gray-900 dark:text-gray-200">
                                    {formatarNota(aluno.detalhes?.comportamento?.base ?? 7.0).replace('.', ',')}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Variação de ocorrências:</span>
                                  <span
                                    className={`font-medium ${
                                      (aluno.detalhes?.comportamento?.saldo ?? 0) >= 0
                                        ? 'text-emerald-600'
                                        : 'text-red-600'
                                    }`}
                                  >
                                    {(aluno.detalhes?.comportamento?.saldo ?? 0) > 0
                                      ? `+${Number(aluno.detalhes?.comportamento?.saldo).toFixed(1)}`
                                      : Number(aluno.detalhes?.comportamento?.saldo ?? 0).toFixed(1)}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Atos de Caráter confirmados:</span>
                                  <span className="font-medium text-emerald-600 dark:text-emerald-400">
                                    {aluno.detalhes?.comportamento?.atos_carater_total ?? 0} (+
                                    {formatarNota(aluno.detalhes?.comportamento?.pontos_carater ?? 0)})
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Lista de Ocorrências e Atos do Aluno */}
                          {(aluno.ocorrencias?.length > 0 || aluno.atos_carater?.length > 0) && (
                            <div className="pt-2 border-t border-gray-100 dark:border-gray-800 space-y-2">
                              <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                                Histórico de Conduta no Período
                              </h4>

                              {/* Ocorrências */}
                              {aluno.ocorrencias?.map((o, i) => (
                                <div
                                  key={`oc-${i}`}
                                  className="flex items-start gap-2 text-xs p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/60"
                                >
                                  <AlertTriangle className="w-3.5 h-3.5 text-gray-400 mt-0.5 shrink-0" />
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <span
                                        className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${corGravidade[o.gravidade] || 'bg-gray-100 text-gray-700'}`}
                                      >
                                        {labelGravidade[o.gravidade] || o.gravidade}
                                      </span>
                                      {o.periodo_nome && (
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
                                          {o.periodo_nome}
                                        </span>
                                      )}
                                      <span className="text-[11px] text-gray-400 dark:text-gray-500">
                                        {o.data_ocorrencia ? new Date(`${o.data_ocorrencia}T00:00:00`).toLocaleDateString('pt-BR') : ''}
                                      </span>
                                    </div>
                                    <p className="text-gray-700 dark:text-gray-300 text-xs mt-0.5">
                                      {o.descricao}
                                    </p>
                                  </div>
                                </div>
                              ))}

                              {/* Atos de Caráter */}
                              {aluno.atos_carater?.map((ato, i) => (
                                <div
                                  key={`ato-${i}`}
                                  className="flex items-start gap-2 text-xs p-2 rounded-lg bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900/40"
                                >
                                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-200">
                                        <Sparkles className="w-2.5 h-2.5" /> Ato de Caráter (+0,2)
                                      </span>
                                      {ato.periodo_nome && (
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
                                          {ato.periodo_nome}
                                        </span>
                                      )}
                                      {ato.titulo_missao && (
                                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                          {ato.titulo_missao}
                                        </span>
                                      )}
                                      {ato.data_realizacao && (
                                        <span className="text-[11px] text-gray-400 dark:text-gray-500">
                                          · {new Date(`${ato.data_realizacao}T00:00:00`).toLocaleDateString('pt-BR')}
                                        </span>
                                      )}
                                    </div>
                                    {ato.observacao && (
                                      <p className="text-gray-600 dark:text-gray-400 text-xs mt-0.5">
                                        {ato.observacao}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <Trophy className="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-gray-700" />
                <p className="text-sm">
                  {busca
                    ? 'Nenhum aluno encontrado para essa busca.'
                    : 'Nenhum aluno ativo encontrado no período selecionado.'}
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default Meritocracia;